import { router } from '@inertiajs/react'

import { useCurrency } from '@/lib/currency'
import { cn } from '@/lib/utils'

/**
 * Four supported markets in a native selector, usable with touch or keyboard.
 *
 * Persists through PATCH /currency (session), then lets the redirect
 * re-render every final market tag on the page.
 */
function CurrencySwitch({
  onDark = false,
  className,
  /** Overrides on the control itself — the chip header restyles it to
   *  match the square chips beside it. */
  selectClassName,
}: {
  onDark?: boolean
  className?: string
  selectClassName?: string
}) {
  const { current, currencies } = useCurrency()

  const switchTo = (next: string) => {
    if (!next || next === current) return
    router.patch('/currency', { currency: next }, { preserveScroll: true })
  }

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <select
        aria-label="Currency"
        value={current}
        onChange={(event) => switchTo(event.target.value)}
        className={cn(
          // bg-none kills the @tailwindcss/forms chevron — this switch
          // draws its own; with both, the arrow doubled.
          'h-10 cursor-pointer appearance-none rounded-sm border bg-transparent bg-none',
          'pl-3 pr-8 text-[14px] font-medium leading-none text-ink',
          'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
          onDark ? 'border-[rgba(245,245,245,0.28)]' : 'border-hairline hover:bg-surface-sunk',
          selectClassName
        )}
      >
        {currencies.map((meta) => (
          <option key={meta.code} value={meta.code} className="bg-surface text-ink">
            {meta.code === 'BYN' ? meta.symbol : `${meta.symbol} ${meta.code}`}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-3 h-1.5 w-1.5 -translate-y-0.5 rotate-45 border-b border-r border-ink-faint"
      />
    </div>
  )
}

export { CurrencySwitch }
